/**
 * CoreTransactionAgGridHook
 *
 * AG Grid hook for the Core Transactions LiveView.
 *
 * Lifecycle
 *  mounted()  – Creates the grid with full column definitions.
 *  updated()  – Refreshes row data when the LiveView re-renders (filter / page server change).
 *  destroyed()– Destroys the grid API to free memory.
 *
 * LiveView events received (via handleEvent):
 *  "update_core_transactions_data" – { rows: [...] }  – pushed by the server after filter/tab change.
 *
 * LiveView events pushed (via pushEvent):
 *  "core_txn_row_clicked" – { id: <id> }  – when a row is selected.
 */

const CoreTransactionAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const hook = this; // Capture hook instance for use in cellRenderers

    const STATUS_COLOURS = {
      unmatched:       "#6B7280",
      matched:         "#2563EB",
      risk_hold:       "#DC2626",
      chargeback_hold: "#EA580C",
      exception_hold:  "#D97706",
      approved:        "#16A34A",
      paid:            "#15803D",
      rejected:        "#7C3AED",
      // QR tab: the Status column carries the payment outcome from the
      // `transactions` table rather than a settlement lifecycle value.
      success:         "#16A34A",
      completed:       "#15803D",
      pending:         "#D97706",
      failed:          "#DC2626",
      error:           "#DC2626",
      declined:        "#B91C1C",
      cancelled:       "#6B7280",
      refunded:        "#2563EB",
    };

    const TYPE_COLOURS = {
      SALE: "#16A34A",
      REFUND: "#2563EB",
      REVERSAL: "#EA580C",
      // Rose reads as "cancelled" and stays distinct from REVERSAL's orange —
      // the old grey (#6B7280) disappeared against the row background.
      VOID: "#E11D48",
      // The switch tables spell these out in s_txn_type; without an entry they
      // fall back to the grey default badge.
      VOID_SALE: "#E11D48",
      PREAUTH: "#0891B2",
      PREAUTH_COMPLETE: "#0E7490",
      BALANCE_INQUIRY: "#8B5CF6",
      // QR tab: Txn Type is pay_mode, falling back to provider_name.
      QR: "#7C3AED",
      "Dynamic QR": "#7C3AED",
      StaticQR: "#6D28D9",
      QR_PAYMENT: "#7C3AED",
      PAYMENT: "#6D28D9",
      Alipay: "#0EA5E9",
      alipay: "#0EA5E9",
      UPI: "#059669",
      upi: "#059669",
      AANI: "#C026D3",
      aani: "#C026D3",
      YSP: "#9333EA",
      Card: "#2563EB",
    };

    const SOURCE_COLOURS = {
      POS: "#0284C7",
      QR: "#7C3AED",
      ECOM: "#DB2777",
      // SoftPOS merges three registers into one list, but Source names the
      // channel here exactly as it does on the tabs above — the register a row
      // came from shows up in Status instead.
      SOFTPOS: "#0D9488",
      // Replaced: only the SoftPOS merge ever set these as a *source*, and an
      // outcome was never a channel. The Failed and Reversal tabs report POS.
      // FAILED: "#DC2626",
      // REVERSAL: "#EA580C",
    };

    const DEFAULT_BADGE_BG = "#9CA3AF";

    const createBadge = (value, color) => {
      if (!value) return "";
      return `<span class="ag-badge" style="background:${color || DEFAULT_BADGE_BG}">${value}</span>`;
    };

    // Replaced: an exact lookup only ever matched the map's own lowercase keys,
    // so every status that carries a reason — the Declined tab's "03 — Invalid
    // Merchant", SoftPOS's "FAILED — Manual cleanup invoked" — fell through to
    // the grey default and lost the one colour that made it scannable.
    // const statusBadge = (value) => createBadge(value, STATUS_COLOURS[value]);
    const statusColour = (v) =>
      STATUS_COLOURS[v] || STATUS_COLOURS[String(v).toLowerCase()];

    // Whole value first, then the word before the em dash separator, so
    // "APPROVED" and "FAILED — <reason>" both land on their colour and anything
    // unrecognised still falls back to grey.
    const statusBadge = (value) => {
      if (!value) return "";
      const head = String(value).split(" — ")[0];
      return createBadge(value, statusColour(value) || statusColour(head));
    };

    const amountCell = (params) => {
      if (params.value === null || params.value === undefined) return "";
      const n = parseFloat(params.value);
      return isNaN(n) ? params.value : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Display label only — TYPE_COLOURS stays keyed on the raw "YSP" data value.
    const typeBadge = (value) => createBadge(value === "YSP" ? "Acquirer/Switch" : value, TYPE_COLOURS[value]);

    const sourceBadge = (value) => createBadge(value, SOURCE_COLOURS[value]);

    const yesFlag = (value, color) =>
      value ? `<span style="color:${color};font-weight:600;">YES</span>` : "";

    // Risk Hold button renderer
    const riskHoldButton = (params) => {
      // Rows from the Refund / Void / Reversal / Failed tabs come out of the
      // raw POS switch tables and carry core_record: false. Manual Risk Hold
      // updates core_transactions by id, so offering it there would hold an
      // unrelated core row that happens to share the id.
      if (params.data && params.data.core_record === false) return null;

      const btn = document.createElement('button');
      btn.textContent = 'Risk Hold';
      btn.className = 'px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500';
      btn.onclick = (e) => {
        e.stopPropagation(); // Prevent row click event
        hook.pushEvent("show_risk_hold_modal", { id: params.data.id });
      };
      return btn;
    };

    const columnDefs = [
      { headerName: "#",                  field: "id",                     width: 80,  pinned: "left", sortable: true, filter: false },
      { headerName: "Source",             field: "source_type",            width: 80,  cellRenderer: p => sourceBadge(p.value) },
      { headerName: "Txn Type",           field: "transaction_type",       width: 100, cellRenderer: p => typeBadge(p.value) },
      { headerName: "RRN",                field: "rrn",                    width: 140 },
      { headerName: "TID",                field: "tid",                    width: 110 },
      { headerName: "Auth #",             field: "auth_number",            width: 100 },
      { headerName: "STAN",               field: "stan",                   width: 90 },
      { headerName: "Invoice #",          field: "invoice_number",         width: 110 },
      { headerName: "Batch #",            field: "batch_number",           width: 100 },
      { headerName: "Merchant MID",       field: "merchant_mid",           width: 150 },
      { headerName: "Merchant Ref",       field: "merchant_ref_number",    width: 130 },
      { headerName: "Amount",             field: "transaction_amount",     width: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "CCY",                field: "transaction_currency",   width: 70  },
      { headerName: "Txn Date",           field: "transaction_datetime",   width: 160, sort: "desc" },
      { headerName: "Masked PAN",         field: "masked_pan",             width: 150 },
      { headerName: "Scheme",             field: "scheme_name",            width: 100 },
      { headerName: "Card Type",          field: "card_type_code",         width: 100 },
      { headerName: "MDR %",              field: "mdr_percentage_rate",    width: 90,  cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "MDR Amount",         field: "mdr_amount",             width: 110, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "VAT",                field: "vat_amount",             width: 90,  cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "Interchange",        field: "interchange_fee",        width: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "Gross Amount",       field: "gross_amount",           width: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "Net Settlement",     field: "net_settlement_amount",  width: 130, cellRenderer: amountCell, type: "numericColumn" },
      // Explicit colId: this shares field "id" with the "#" column, so without
      // one AG Grid derives "id_1" and the server could not name it in
      // hidden_columns.
      { headerName: "Risk Hold Action",   field: "id", colId: "risk_hold_action", width: 130, pinned: "right", sortable: false, filter: false, cellRenderer: riskHoldButton },
      { headerName: "Status",             field: "settlement_status",      width: 140, pinned: "right", cellRenderer: p => statusBadge(p.value) },
      { headerName: "Switch Settled",     field: "switch_settled_date",    width: 130 },
      { headerName: "Risk Hold",          field: "risk_hold",              width: 90,
        cellRenderer: p => yesFlag(p.value, STATUS_COLOURS.risk_hold) },
      { headerName: "Risk Rule",          field: "risk_rule_name",         width: 130 },
      { headerName: "CB Hold",            field: "chargeback_hold",        width: 90,
        cellRenderer: p => yesFlag(p.value, STATUS_COLOURS.chargeback_hold) },
      { headerName: "Refund Status",      field: "refund_status",          width: 110 },
      { headerName: "DR/CR",              field: "dr_cr",                  width: 80  },
      { headerName: "Batch ID",           field: "settlement_batch_id",    width: 180 },
      { headerName: "Bank Ref Number",    field: "bank_transfer_reference", width: 150 },
      { headerName: "Bank Txn Date",      field: "bank_transfer_date",      width: 130 },
      { headerName: "Inserted At",        field: "inserted_at",            width: 160 },
    ];

    const gridOptions = {
      columnDefs: columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50, 100],
      popupParent: document.body,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 70,
      },
      animateRows: true,
      rowSelection: "single",
      suppressPivotMode: true,
      suppressRowGroupsPanel: true,
      suppressRowGroupHidesColumns: true,
      suppressGroupRowsStickiness: true,
      suppressAggFuncInHeader: true,
      suppressColumnStateEvents: false,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false
            }
          }
        ]
      },
      onRowClicked: (event) => {
        if (event.data && event.data.id) {
          // The POS channel page merges several switch tables into one tab, and
          // their primary keys overlap, so its rows carry a table-qualified
          // `uid`. Pages whose rows come from a single table have no `uid` and
          // keep identifying rows by `id` exactly as before.
          this.pushEvent("core_txn_row_clicked", { id: event.data.uid || event.data.id });
        }
      },
      overlayNoRowsTemplate:
        `<span class="ag-no-rows-overlay">No core transactions found. Adjust filters or trigger a batch sync.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
      this.gridOptions = gridOptions;
      this.applyHiddenColumns(this.parseHidden(container.dataset.hiddenColumns));
    } else {
      console.error("CoreTransactionAgGridHook: agGrid not found on window");
    }

    // Server-push update (e.g. after filter change, record tab switch)
    this.handleEvent("update_core_transactions_data", ({ rows: newRows, hidden_columns }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
        this.applyHiddenColumns(hidden_columns || []);
      }
    });
  },

  updated() {
    // Fallback: if LiveView re-renders with new data-rows attribute, refresh
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
        this.applyHiddenColumns(this.parseHidden(this.el.dataset.hiddenColumns));
      } catch (e) {
        console.warn("CoreTransactionAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  parseHidden(raw) {
    try {
      return JSON.parse(raw || "[]");
    } catch (e) {
      return [];
    }
  },

  // Each record tab fills a different subset of the columns, so the server
  // sends the ones that are empty for the rows on screen and they are hidden
  // rather than shown as a run of blank cells. Always reset to fully visible
  // first — a column empty on the last tab may carry data on this one.
  //
  // Anything hidden here is still listed in the grid's Columns side panel, so
  // it can be switched back on by hand.
  applyHiddenColumns(hidden) {
    if (!this.gridApi) return;

    const columns = this.gridApi.getColumns() || [];
    if (columns.length === 0) return;

    const hide = new Set(hidden || []);
    const allIds = columns.map((col) => col.getColId());

    this.gridApi.setColumnsVisible(allIds, true);
    if (hide.size > 0) {
      this.gridApi.setColumnsVisible(allIds.filter((id) => hide.has(id)), false);
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default CoreTransactionAgGridHook;
